/**
 * Contratos do ZapOrca sem dependência de runtime.
 * Os objetos *.Schema expõem safeParse/parse no formato { success, data | error.issues }.
 */

export type TipoChavePix = 'cpf' | 'cnpj' | 'email' | 'telefone' | 'aleatoria';
export type TipoItem = 'servico' | 'material';
export type OrcamentoStatus =
  | 'rascunho'
  | 'enviado'
  | 'visualizado'
  | 'aprovado'
  | 'recusado'
  | 'sinal_pago'
  | 'concluido';

export interface PerfilMei {
  id: string;
  nome: string;
  nomeFantasia?: string;
  documento: string;
  telefone: string;
  email?: string;
  chavePix: string;
  tipoChavePix: TipoChavePix;
  cidade: string;
  uf: string;
  ramoAtividade: string;
  logoUrl?: string;
}

export interface Cliente {
  id: string;
  nome: string;
  telefone: string;
  email?: string;
  endereco?: string;
  cidade?: string;
  documento?: string;
}

export interface ItemOrcamento {
  id: string;
  descricao: string;
  quantidade: number;
  unidade: string;
  valorUnitario: number;
  tipo: TipoItem;
  subtotal: number;
}

export interface HistoricoEvento {
  dataHora: string;
  tipo: string;
  descricao: string;
}

export interface PixPayload {
  chavePix: string;
  tipoChave: TipoChavePix;
  nomeRecebedor: string;
  cidadeRecebedor: string;
  valor: number;
  txId: string;
  payloadFormatado: string;
  urlQrCode?: string;
}

export interface TransacaoPix {
  id: string;
  orcamentoId: string;
  valor: number;
  status: 'pendente' | 'confirmada' | 'expirada';
  dataHora: string;
  e2eId?: string;
  comprovanteUrl?: string;
}

export interface Orcamento {
  id: string;
  uuid: string;
  numero: string;
  titulo: string;
  dataCriacao: string;
  dataValidade: string;
  perfilMei: PerfilMei;
  cliente: Cliente;
  itens: ItemOrcamento[];
  valorSubtotal: number;
  desconto: number;
  tipoDesconto: 'fixo' | 'percentual';
  valorTotal: number;
  percentualSinal: number;
  valorSinal: number;
  valorRestante: number;
  observacoes?: string;
  condicoesPagamento?: string;
  prazoEstimado?: string;
  status: OrcamentoStatus;
  linkPublico: string;
  payloadPix?: PixPayload;
  historicoEventos: HistoricoEvento[];
  dataAprovacao?: string;
  dataPagamentoSinal?: string;
}

export type CreateOrcamentoInput = Omit<
  Orcamento,
  | 'id'
  | 'uuid'
  | 'numero'
  | 'dataCriacao'
  | 'valorSubtotal'
  | 'valorTotal'
  | 'valorSinal'
  | 'valorRestante'
  | 'linkPublico'
  | 'historicoEventos'
> & { status?: OrcamentoStatus };

export type OutboxAction =
  | 'UPSERT_ORCAMENTO'
  | 'DELETE_ORCAMENTO'
  | 'UPSERT_PERFIL'
  | 'PIX_PAYMENT_CONFIRMED';

export interface OutboxItem {
  id: string;
  entityId: string;
  action: OutboxAction;
  payload: any;
  timestamp: string;
  synced: boolean;
  retries: number;
  lastError?: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  nomeCompleto: string;
  aceiteLgpd: true;
}

export interface AuthUser {
  id: string;
  email: string;
  nome?: string;
  lgpdConsentAt: string;
  lgpdVersion: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface SyncResult {
  synced: boolean;
  pulledCount: number;
  pushedCount: number;
  error?: string;
}

export interface PerfilEmpresaInput {
  nome: string;
  nomeFantasia?: string;
  documento: string;
  telefone: string;
  email?: string;
  chavePix: string;
  tipoChavePix: TipoChavePix;
  cidade: string;
  uf: string;
  ramoAtividade: string;
  logoUrl?: string;
}

export interface DeleteAccountInput {
  confirmEmail: string;
  expectedEmail: string;
}

export interface SchemaIssue {
  message: string;
}

export type SafeParseResult<T> =
  | { success: true; data: T }
  | { success: false; error: { issues: SchemaIssue[] } };

function ok<T>(data: T): SafeParseResult<T> {
  return { success: true, data };
}

function fail<T>(messages: string[]): SafeParseResult<T> {
  return { success: false, error: { issues: messages.map(message => ({ message })) } };
}

export interface Schema<T> {
  safeParse(input: unknown): SafeParseResult<T>;
  parse(input: unknown): T;
}

function schema<T>(validate: (input: unknown) => SafeParseResult<T>): Schema<T> {
  return {
    safeParse: validate,
    parse(input: unknown): T {
      const result = validate(input);
      if (!result.success) {
        throw new Error(result.error.issues[0]?.message || 'Dados inválidos');
      }
      return result.data;
    }
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

const TIPOS_CHAVE: readonly TipoChavePix[] = ['cpf', 'cnpj', 'email', 'telefone', 'aleatoria'];
const STATUS: readonly OrcamentoStatus[] = [
  'rascunho', 'enviado', 'visualizado', 'aprovado', 'recusado', 'sinal_pago', 'concluido'
];

function optionalEmail(value: unknown, invalidMessage: string): string | null {
  if (value === undefined || value === '') return null;
  if (typeof value !== 'string' || !isEmail(value)) return invalidMessage;
  return null;
}

export const RegisterInputSchema = schema<RegisterInput>((input) => {
  if (!isRecord(input)) return fail(['Dados de cadastro inválidos']);
  const issues: string[] = [];
  if (typeof input.email !== 'string' || !isEmail(input.email)) {
    issues.push('Informe um e-mail válido');
  }
  if (typeof input.password !== 'string' || input.password.length < 6) {
    issues.push('A senha deve ter no mínimo 6 caracteres');
  }
  if (typeof input.nomeCompleto !== 'string' || input.nomeCompleto.trim().length < 3) {
    issues.push('Nome completo deve ter no mínimo 3 caracteres');
  }
  if (input.aceiteLgpd !== true) {
    issues.push('Você deve concordar com os Termos de Uso e Política de Privacidade (LGPD).');
  }
  if (issues.length > 0) return fail(issues);
  return ok({
    email: input.email as string,
    password: input.password as string,
    nomeCompleto: input.nomeCompleto as string,
    aceiteLgpd: true
  });
});

export const LoginInputSchema = schema<LoginInput>((input) => {
  if (!isRecord(input)) return fail(['Dados de login inválidos']);
  const issues: string[] = [];
  if (typeof input.email !== 'string' || !isEmail(input.email)) {
    issues.push('Informe um e-mail válido');
  }
  if (typeof input.password !== 'string' || input.password.length < 1) {
    issues.push('A senha é obrigatória');
  }
  if (issues.length > 0) return fail(issues);
  return ok({ email: input.email as string, password: input.password as string });
});

export const AuthUserSchema = schema<AuthUser>((input) => {
  if (!isRecord(input)) return fail(['Sessão inválida']);
  if (typeof input.id !== 'string' || input.id.length === 0) return fail(['Sessão inválida']);
  if (typeof input.email !== 'string' || !isEmail(input.email)) return fail(['E-mail inválido']);
  if (typeof input.lgpdConsentAt !== 'string' || input.lgpdConsentAt.length === 0) {
    return fail(['Consentimento LGPD ausente']);
  }
  const nome = typeof input.nome === 'string' ? input.nome : undefined;
  const lgpdVersion = typeof input.lgpdVersion === 'string' && input.lgpdVersion.length > 0
    ? input.lgpdVersion
    : '1.0';
  return ok({
    id: input.id,
    email: input.email,
    nome,
    lgpdConsentAt: input.lgpdConsentAt,
    lgpdVersion
  });
});

export const DeleteAccountInputSchema = schema<DeleteAccountInput>((input) => {
  if (!isRecord(input)) return fail(['E-mail inválido']);
  const confirmRaw = typeof input.confirmEmail === 'string' ? input.confirmEmail.trim() : '';
  const expectedRaw = typeof input.expectedEmail === 'string' ? input.expectedEmail.trim() : '';
  if (!isEmail(confirmRaw)) return fail(['E-mail inválido']);
  if (!isEmail(expectedRaw)) return fail(['E-mail inválido']);
  if (confirmRaw.toLowerCase() !== expectedRaw.toLowerCase()) {
    return fail(['O e-mail digitado não corresponde ao e-mail da sua conta']);
  }
  return ok({ confirmEmail: confirmRaw, expectedEmail: expectedRaw });
});

export const PerfilEmpresaInputSchema = schema<PerfilEmpresaInput>((input) => {
  if (!isRecord(input)) return fail(['Perfil inválido']);
  const issues: string[] = [];
  if (typeof input.nome !== 'string' || input.nome.trim().length < 3) {
    issues.push('Razão social / Nome deve ter pelo menos 3 caracteres');
  }
  if (typeof input.documento !== 'string' || input.documento.trim().length < 11) {
    issues.push('Informe um CPF ou CNPJ válido');
  }
  if (typeof input.telefone !== 'string' || input.telefone.length < 10) {
    issues.push('Telefone com DDD é obrigatório');
  }
  const emailIssue = optionalEmail(input.email, 'E-mail inválido');
  if (emailIssue) issues.push(emailIssue);
  if (typeof input.chavePix !== 'string' || input.chavePix.trim().length < 1) {
    issues.push('Chave Pix é obrigatória');
  }
  if (typeof input.tipoChavePix !== 'string' || !TIPOS_CHAVE.includes(input.tipoChavePix as TipoChavePix)) {
    issues.push('Tipo de chave Pix inválido');
  }
  if (typeof input.cidade !== 'string' || input.cidade.trim().length < 2) {
    issues.push('Cidade é obrigatória');
  } else if (input.cidade.trim().length > 15) {
    issues.push('Cidade deve ter no máximo 15 caracteres para o Pix');
  }
  if (typeof input.uf !== 'string' || input.uf.trim().length !== 2) {
    issues.push('UF deve conter 2 letras (ex: SP)');
  }
  if (typeof input.ramoAtividade !== 'string' || input.ramoAtividade.trim().length < 2) {
    issues.push('Ramo de atividade é obrigatório');
  }
  if (issues.length > 0) return fail(issues);
  const data: PerfilEmpresaInput = {
    nome: (input.nome as string).trim(),
    documento: (input.documento as string).trim(),
    telefone: input.telefone as string,
    chavePix: (input.chavePix as string).trim(),
    tipoChavePix: input.tipoChavePix as TipoChavePix,
    cidade: (input.cidade as string).trim(),
    uf: (input.uf as string).trim(),
    ramoAtividade: (input.ramoAtividade as string).trim()
  };
  if (typeof input.nomeFantasia === 'string') data.nomeFantasia = input.nomeFantasia;
  if (typeof input.email === 'string') data.email = input.email;
  if (typeof input.logoUrl === 'string') data.logoUrl = input.logoUrl;
  return ok(data);
});

function validatePerfilMei(input: unknown): SafeParseResult<PerfilMei> {
  if (!isRecord(input)) return fail(['Perfil do prestador inválido']);
  const issues: string[] = [];
  if (typeof input.nome !== 'string' || input.nome.trim().length < 3) {
    issues.push('Nome deve ter pelo menos 3 caracteres');
  }
  if (typeof input.documento !== 'string' || input.documento.trim().length < 11) {
    issues.push('Documento inválido');
  }
  if (typeof input.telefone !== 'string' || input.telefone.length < 10) {
    issues.push('Telefone com DDD é obrigatório');
  }
  const emailIssue = optionalEmail(input.email, 'E-mail inválido');
  if (emailIssue) issues.push(emailIssue);
  if (typeof input.chavePix !== 'string' || input.chavePix.trim().length < 5) {
    issues.push('Chave Pix é obrigatória');
  }
  const tipo = (typeof input.tipoChavePix === 'string' ? input.tipoChavePix : 'telefone') as TipoChavePix;
  if (!TIPOS_CHAVE.includes(tipo)) issues.push('Tipo de chave Pix inválido');
  if (typeof input.cidade !== 'string' || input.cidade.trim().length < 2) {
    issues.push('Cidade é obrigatória');
  } else if (input.cidade.trim().length > 15) {
    issues.push('Cidade deve ter no máximo 15 caracteres no Pix');
  }
  if (typeof input.uf !== 'string' || input.uf.trim().length !== 2) {
    issues.push('UF deve ter 2 caracteres');
  }
  if (typeof input.ramoAtividade !== 'string' || input.ramoAtividade.trim().length < 2) {
    issues.push('Ramo de atividade é obrigatório');
  }
  if (issues.length > 0) return fail(issues);
  return ok({
    id: typeof input.id === 'string' && input.id.length > 0 ? input.id : 'mei-default',
    nome: (input.nome as string).trim(),
    nomeFantasia: typeof input.nomeFantasia === 'string' ? input.nomeFantasia : undefined,
    documento: (input.documento as string).trim(),
    telefone: input.telefone as string,
    email: typeof input.email === 'string' ? input.email : undefined,
    chavePix: (input.chavePix as string).trim(),
    tipoChavePix: tipo,
    cidade: (input.cidade as string).trim(),
    uf: (input.uf as string).trim(),
    ramoAtividade: (input.ramoAtividade as string).trim(),
    logoUrl: typeof input.logoUrl === 'string' ? input.logoUrl : undefined
  });
}

function validateCliente(input: unknown): SafeParseResult<Cliente> {
  if (!isRecord(input)) return fail(['Cliente inválido']);
  const issues: string[] = [];
  if (typeof input.nome !== 'string' || input.nome.trim().length < 2) {
    issues.push('Nome do cliente é obrigatório');
  }
  if (typeof input.telefone !== 'string' || input.telefone.length < 8) {
    issues.push('Telefone do cliente é obrigatório');
  }
  const emailIssue = optionalEmail(input.email, 'E-mail inválido');
  if (emailIssue) issues.push(emailIssue);
  if (issues.length > 0) return fail(issues);
  return ok({
    id: typeof input.id === 'string' && input.id.length > 0 ? input.id : `cli-${Date.now()}`,
    nome: (input.nome as string).trim(),
    telefone: input.telefone as string,
    email: typeof input.email === 'string' ? input.email : undefined,
    endereco: typeof input.endereco === 'string' ? input.endereco : undefined,
    cidade: typeof input.cidade === 'string' ? input.cidade : undefined,
    documento: typeof input.documento === 'string' ? input.documento : undefined
  });
}

function validateItem(input: unknown): SafeParseResult<ItemOrcamento> {
  if (!isRecord(input)) return fail(['Item inválido']);
  const issues: string[] = [];
  if (typeof input.descricao !== 'string' || input.descricao.trim().length < 2) {
    issues.push('Descrição do item é obrigatória');
  }
  if (typeof input.quantidade !== 'number' || !(input.quantidade > 0)) {
    issues.push('Quantidade deve ser maior que zero');
  }
  if (typeof input.valorUnitario !== 'number' || input.valorUnitario < 0) {
    issues.push('Valor unitário não pode ser negativo');
  }
  if (typeof input.subtotal !== 'number' || input.subtotal < 0) {
    issues.push('Subtotal inválido');
  }
  const tipo = (typeof input.tipo === 'string' ? input.tipo : 'servico') as TipoItem;
  if (tipo !== 'servico' && tipo !== 'material') issues.push('Tipo de item inválido');
  if (issues.length > 0) return fail(issues);
  return ok({
    id: typeof input.id === 'string' && input.id.length > 0
      ? input.id
      : `item-${Math.random().toString(36).substring(2, 9)}`,
    descricao: (input.descricao as string).trim(),
    quantidade: input.quantidade as number,
    unidade: typeof input.unidade === 'string' && input.unidade.length > 0 ? input.unidade : 'un',
    valorUnitario: input.valorUnitario as number,
    tipo,
    subtotal: input.subtotal as number
  });
}

export const OrcamentoSchema = schema<Orcamento>((input) => {
  if (!isRecord(input)) return fail(['Orçamento inválido']);
  const issues: string[] = [];
  if (typeof input.id !== 'string' || input.id.length === 0) issues.push('Identificador obrigatório');
  if (typeof input.uuid !== 'string' || input.uuid.length === 0) issues.push('UUID obrigatório');
  if (typeof input.numero !== 'string' || input.numero.length === 0) issues.push('Número obrigatório');
  if (typeof input.titulo !== 'string' || input.titulo.trim().length < 3) {
    issues.push('Título da proposta é obrigatório');
  }
  if (typeof input.dataCriacao !== 'string') issues.push('Data de criação obrigatória');
  if (typeof input.dataValidade !== 'string') issues.push('Data de validade obrigatória');
  if (typeof input.linkPublico !== 'string') issues.push('Link público obrigatório');

  const perfil = validatePerfilMei(input.perfilMei);
  if (!perfil.success) issues.push(...perfil.error.issues.map(i => i.message));
  const cliente = validateCliente(input.cliente);
  if (!cliente.success) issues.push(...cliente.error.issues.map(i => i.message));

  const itensBrutos = Array.isArray(input.itens) ? input.itens : [];
  if (itensBrutos.length < 1) issues.push('Adicione pelo menos 1 item ou serviço');
  const itens: ItemOrcamento[] = [];
  for (const item of itensBrutos) {
    const parsed = validateItem(item);
    if (!parsed.success) issues.push(...parsed.error.issues.map(i => i.message));
    else itens.push(parsed.data);
  }

  const numeros: Array<[unknown, string]> = [
    [input.valorSubtotal, 'Subtotal inválido'],
    [input.valorTotal, 'Total inválido'],
    [input.valorSinal, 'Sinal inválido'],
    [input.valorRestante, 'Restante inválido']
  ];
  for (const [valor, mensagem] of numeros) {
    if (typeof valor !== 'number' || valor < 0) issues.push(mensagem);
  }
  const desconto = typeof input.desconto === 'number' ? input.desconto : input.desconto === undefined ? 0 : Number.NaN;
  if (typeof desconto !== 'number' || desconto < 0 || Number.isNaN(desconto)) issues.push('Desconto inválido');
  const tipoDesconto: 'fixo' | 'percentual' = input.tipoDesconto === 'percentual' ? 'percentual' : 'fixo';
  if (input.tipoDesconto !== undefined && input.tipoDesconto !== 'fixo' && input.tipoDesconto !== 'percentual') {
    issues.push('Tipo de desconto inválido');
  }
  const percentualSinal = typeof input.percentualSinal === 'number'
    ? input.percentualSinal
    : input.percentualSinal === undefined ? 30 : Number.NaN;
  if (typeof percentualSinal !== 'number' || Number.isNaN(percentualSinal) || percentualSinal < 0 || percentualSinal > 100) {
    issues.push('Percentual do sinal deve ficar entre 0 e 100');
  }
  const status = (input.status === undefined ? 'rascunho' : input.status) as OrcamentoStatus;
  if (!STATUS.includes(status)) issues.push('Status inválido');

  if (issues.length > 0 || !perfil.success || !cliente.success) return fail(issues);

  const historico: HistoricoEvento[] = [];
  if (Array.isArray(input.historicoEventos)) {
    for (const evento of input.historicoEventos) {
      if (!isRecord(evento) || typeof evento.dataHora !== 'string' || typeof evento.tipo !== 'string' || typeof evento.descricao !== 'string') {
        return fail(['Histórico inválido']);
      }
      historico.push({ dataHora: evento.dataHora, tipo: evento.tipo, descricao: evento.descricao });
    }
  }

  return ok({
    id: input.id as string,
    uuid: input.uuid as string,
    numero: input.numero as string,
    titulo: (input.titulo as string).trim(),
    dataCriacao: input.dataCriacao as string,
    dataValidade: input.dataValidade as string,
    perfilMei: perfil.data,
    cliente: cliente.data,
    itens,
    valorSubtotal: input.valorSubtotal as number,
    desconto,
    tipoDesconto,
    valorTotal: input.valorTotal as number,
    percentualSinal,
    valorSinal: input.valorSinal as number,
    valorRestante: input.valorRestante as number,
    observacoes: typeof input.observacoes === 'string' ? input.observacoes : undefined,
    condicoesPagamento: typeof input.condicoesPagamento === 'string' ? input.condicoesPagamento : undefined,
    prazoEstimado: typeof input.prazoEstimado === 'string' ? input.prazoEstimado : undefined,
    status,
    linkPublico: input.linkPublico as string,
    historicoEventos: historico,
    dataAprovacao: typeof input.dataAprovacao === 'string' ? input.dataAprovacao : undefined,
    dataPagamentoSinal: typeof input.dataPagamentoSinal === 'string' ? input.dataPagamentoSinal : undefined
  });
});
